import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import queryString from 'query-string';

import { ApplicationName, GenericStep } from 'components/CASAForm';
import { AuCallout } from 'components/DTA';
import { Loader } from 'components/common';
import {
  DroneContext,
  FormContext,
  OrganisationContext
}
  from 'contexts';
import { DroneRegistrationFormData } from 'types/forms/droneRegistrationTypes';
import { useHmacService } from 'services';
import { processUrlParameters } from 'utilities';

const BeginStep: React.FC = () => {
  const userDetails = useSelector((state: any) => state.userdetails);
  const { isOrganisationRegistration, isFeatureRecDrone, dashboardUrl } = useContext(DroneContext);
  const { organisationDetailsResponse } = useContext(OrganisationContext);
  const { values } = useFormikContext<DroneRegistrationFormData>();
  const { openStepById, setFormAlert } = useContext(FormContext);
  const hmacService = useHmacService();
  const [hmacChecked, setHmacChecked] = useState(false);

  // runs on the first render
  useEffect(() => {
    // if drones exist then application is considered a draft, therefore proceed to the next step
    if (values.drones.length > 0) {
      openStepById('DroneEdit');
    }

    const processHmac = async (returnParameters: any) => {
      try {
        const response = await hmacService.validateHmac({ paymentParamters: returnParameters });
        openStepById('Final', { successful: response?.wasSuccessful && response?.hmacValid && response?.paymentSuccess });
      }
      catch (error) {
        openStepById('Final', { successful: false });
      }
    };
    const returnParameters = queryString.parse(window.location.search);
    if (returnParameters.summaryCode === '0') {
      processHmac(returnParameters);
    }
    else {
      setHmacChecked(true);
    }
    setFormAlert(null);
    const params = processUrlParameters(window.location.search);
    if (params && params.draftdeleted) {
      setFormAlert({
        as: 'success',
        title: 'Saved application deleted',
        message: 'Your saved application has been deleted'
      });
    }
    if (returnParameters.dronedereged === '1') {
      setFormAlert({
        as: 'success',
        title: 'Drone de-registered',
        message: `A confirmation of your de-registration has been sent to your email address ${userDetails.items.value[0].emailaddress1}`
      });
    }
    window.scrollTo(0, 0);
  }, []);

  if (!hmacChecked) {
    return <Loader message='Working. Please wait.....' />;
  }

  return (
    <>
      <ApplicationName name='Drone registration' />
      <GenericStep
        nextButtonText='Begin drone registration'
        showSaveAndExit={false}
        customHandleBack={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          window.location.href = dashboardUrl;
        }}
      >
        <AuCallout>
          {isFeatureRecDrone &&
            <>
              <h4>Drone registration</h4>
              <p>There are two types of registration:</p>
              <ul>
                <li><strong>Recreational</strong></li>
                <li><strong>Commercial</strong></li>
              </ul>
              <p><strong>Recreational drone registration</strong> applies to drones or model aircraft that weigh more than 250 grams that are operated for sport, education or recreation. Drones or model aircraft that weigh 250 grams or less do not need to be registered.</p>
              <p><strong>Commercial drone registration</strong> applies to all drones or RPA that are operated for business or as part of your job, regardless of its weight.</p>
              <p>More information about drone registration is available on the <a href="https://www.casa.gov.au/drones/register" target="_blank" rel="noopener noreferrer">CASA website</a>.</p>
            </>}
          {!isFeatureRecDrone &&
            <p>
              Drones or <abbr title='Remotely piloted aircraft'>RPA</abbr> should be registered by the owner.
              If the drone or RPA is owned by an organisation, the registration should be in the name of the organisation using the organisation&apos;s ARN.
              For more information on organisation ARNs, please refer to the <a href="http://casa.gov.au/drone-registration-orgs" target="_blank" rel="noopener noreferrer">CASA website</a>.
            </p>}
          <p>
            {isOrganisationRegistration
              ? <strong>These drones will be registered to {organisationDetailsResponse?.value?.value[0]?.name?.toUpperCase()}, ARN: {organisationDetailsResponse?.value?.value[0]?.casa_arn}</strong>
              : <strong>These drones will be registered to {userDetails?.items?.value[0]?.fullname?.toUpperCase()}, ARN: {userDetails?.items?.value[0]?.casa_arn}</strong>}
          </p>
        </AuCallout>
      </GenericStep>
    </>
  );
};

export default BeginStep;